import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { UpdateProductTypeMappings } from '@repo/backend/crt'
import { PRODUCT_TYPE_CATEGORIES } from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

/** `none` = the variant's SKUs resolve to no product category (rows the table shows with an empty category). */
export const PRODUCT_TYPE_MAPPING_CATEGORY_FILTERS = ['VAPOUR', 'NRT', 'none'] as const
export type ProductTypeMappingCategoryFilter =
  (typeof PRODUCT_TYPE_MAPPING_CATEGORY_FILTERS)[number]

// The mapping page filters client-side; the URL carries the applied filters so a link can pre-filter it.
export const productTypeMappingsSearchSchema = z.object({
  keyword: z.string().optional(),
  productCategory: z.array(z.enum(PRODUCT_TYPE_MAPPING_CATEGORY_FILTERS)).optional(),
})

export type ProductTypeMappingsSearch = z.infer<typeof productTypeMappingsSearchSchema>

export type ProductTypesList = Awaited<ReturnType<typeof getProductTypesList>>
export type ProductType = ProductTypesList['results'][number]

export type ProductTypeMappingsList = Awaited<ReturnType<typeof getProductTypeMappings>>
export type ProductTypeMappingRow = ProductTypeMappingsList['results'][number]

export type ProductTypeMappingEdit = {
  productTypeId: number | null
  isIgnored: boolean
}

async function getProductTypesList(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['product-types'].$get({ query: {} }, { init: { signal } })
  return await res.json()
}

async function getProductTypeMappings(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['product-types'].mappings.$get(
    { query: stringifyValues({ productCategories: PRODUCT_TYPE_CATEGORIES }) },
    { init: { signal } },
  )
  return await res.json()
}

async function getUnmappedProductTypeCount(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['product-types'].mappings['unmapped-count'].$get(
    {},
    { init: { signal } },
  )
  return await res.json()
}

export const unmappedProductTypeCountQueryOptions = {
  queryKey: ['productTypeMappings', 'unmapped-count'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getUnmappedProductTypeCount(signal),
}

export function useUnmappedProductTypeCount() {
  return useQuery(unmappedProductTypeCountQueryOptions)
}

async function getProductTypeMappingsLastUpdated(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['product-types'].mappings['last-updated'].$get({}, { init: { signal } })
  return await res.json()
}

async function updateProductTypeMappings(data: UpdateProductTypeMappings) {
  const api = getApi()
  const res = await api.crt['product-types'].mappings.$patch({ json: data })
  return await res.json()
}

export function useProductTypesList() {
  return useQuery({
    queryKey: ['productTypes'],
    queryFn: ({ signal }) => getProductTypesList(signal),
    refetchOnWindowFocus: false,
  })
}

export function useProductTypeMappings() {
  return useQuery({
    queryKey: ['productTypeMappings'],
    queryFn: ({ signal }) => getProductTypeMappings(signal),
    refetchOnWindowFocus: false,
  })
}

export function useProductTypeMappingsLastUpdated() {
  return useQuery({
    queryKey: ['productTypeMappingsLastUpdated'],
    queryFn: ({ signal }) => getProductTypeMappingsLastUpdated(signal),
  })
}

export function useUpdateProductTypeMappings({ handleOnSuccess }: QueryHandlers = {}) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateProductTypeMappings'],
    mutationFn: (data: UpdateProductTypeMappings) => updateProductTypeMappings(data),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({ queryKey: ['productTypeMappings'] })
      queryClient.invalidateQueries({ queryKey: ['productTypeMappingsLastUpdated'] })
      queryClient.invalidateQueries({ queryKey: ['lastUpdatedProductTypeMappingsLog'] })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('productTypes.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(
        issues,
        t,
        'errors.productTypeMappings.updateFailed',
        'crt',
      )

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
